/**
 * Composant MainLayout
 *
 * Layout principal de l'application avec sidebar
 * Gère l'état collapsed de la sidebar et l'espacement du contenu
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, RefreshCw } from 'lucide-react';
import Sidebar from './Sidebar';
import DashboardTour from './DashboardTour';
import { getInterfaceGuide } from './interfaceGuides';
import '../../styles/appleMotion.css';

const MainLayout = ({ isAdmin = false, children, user, onLogout, onRefresh, isRefreshing = false, openTicketsCount = 0, activeUserStoriesCount = 0, testClientStoriesCount = 0, isInternalUser = false }) => {
  const pathname = useLocation().pathname;
  const guide = getInterfaceGuide(pathname);
  const isHorizonHome = ['/dashboard', '/dashboard/tickets', '/dashboard/client-testing', '/dashboard/ticket-situation', '/dashboard/user-stories-status', '/dashboard/milestone-stories', '/dashboard/deploiements', '/dashboard/livraisons', '/dashboard/chatbot'].includes(pathname) || (!isInternalUser && ['/', '/dashboard/documentation'].includes(pathname));
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const tourStorageKey = user?.uid != null && guide ? `interface-tour-v2:${user.uid}:${pathname}` : null;

  useEffect(() => {
    if (!tourStorageKey) return;
    try {
      const alreadySeen = localStorage.getItem(tourStorageKey) === 'done';
      setTourOpen(!alreadySeen);
    } catch (error) {
      setTourOpen(true);
    }
  }, [pathname, tourStorageKey, user?.uid]);

  const startTour = () => {
    setIsMobileMenuOpen(false);
    setTourOpen(true);
  };

  const closeTour = () => {
    setTourOpen(false);
    if (tourStorageKey) {
      try { localStorage.setItem(tourStorageKey, 'done'); } catch (error) { /* Le guide reste accessible depuis le menu. */ }
    }
  };

  return (
    <div className={`dashboard-shell h-screen overflow-hidden bg-[#F5F6F8] ${isHorizonHome ? 'horizon-layout' : ''}`}>
      <header className="fixed inset-x-0 top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/95 px-4 backdrop-blur lg:hidden">
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(true)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-700 hover:bg-slate-100"
          aria-label="Ouvrir la navigation"
        >
          <Menu className="h-5 w-5" />
        </button>
        <img src="/assets/logo.png" alt="Dynamic Horizon" className="h-8 w-auto" />
        <button
          type="button"
          onClick={onRefresh}
          disabled={!onRefresh || isRefreshing}
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-[#523467] hover:bg-[#F2EDF5] disabled:opacity-40"
          aria-label="Actualiser les données"
        >
          <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </header>

      {isMobileMenuOpen && (
        <button
          type="button"
          aria-label="Fermer la navigation"
          onClick={() => setIsMobileMenuOpen(false)}
          className="fixed inset-0 z-40 bg-slate-950/45 backdrop-blur-[2px] lg:hidden"
        />
      )}

      {/* Sidebar */}
      <Sidebar
        isAdmin={isAdmin}
        user={user}
        isCollapsed={!isSidebarExpanded && !isMobileMenuOpen}
        onExpand={() => setIsSidebarExpanded(true)}
        onCollapse={() => setIsSidebarExpanded(false)}
        onLogout={onLogout}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        openTicketsCount={openTicketsCount}
        activeUserStoriesCount={activeUserStoriesCount}
        testClientStoriesCount={testClientStoriesCount}
        isInternalUser={isInternalUser}
        isMobileOpen={isMobileMenuOpen}
        onMobileClose={() => setIsMobileMenuOpen(false)}
        onStartTour={guide ? startTour : undefined}
      />

      {/* Main Content */}
      <main
        className={`
          h-full overflow-y-auto pt-16 transition-[margin] duration-300 lg:pt-0
          lg:ml-20
        `}
      >
        <div key={pathname} className="dashboard-route-stage">
          {children}
        </div>
      </main>
      {tourOpen && guide && <DashboardTour key={pathname} guide={guide} onClose={closeTour} />}
    </div>
  );
};

export default MainLayout;
